import { createFileRoute, Link } from "@tanstack/react-router";
import portrait from "@/assets/portrait.webp?w=480;800;1100&format=webp&as=picture";
import { projects, brands } from "@/lib/portfolio";
import { internalToPublicSlug } from "@/lib/work-data";
import {
  ArrowUpRight,
  ArrowRight,
  Megaphone,
  Linkedin,
  Mail,
  Youtube,
  Facebook,
  Rss,
  CalendarDays,
  BookOpen,
} from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { BlueskyIcon } from "@/components/BlueskyIcon";
import { lazy, Suspense, type ReactNode } from "react";
import { LazyOnView } from "@/components/LazyOnView";
import { Reveal } from "@/components/Reveal";
import { StatTile } from "@/components/StatTile";
import { ParallaxTilt } from "@/components/ParallaxTilt";
import { useInteractiveMarquee } from "@/hooks/use-interactive-marquee";
import { readingTimeMinutes } from "@/lib/reading-time";
import scottAvatar from "@/assets/scott-avatar.webp";
import { transformSupabaseImage, supabaseSrcSet } from "@/lib/supabase-image";
const Testimonials = lazy(() =>
  import("@/components/Testimonials").then((m) => ({ default: m.Testimonials })),
);
const ServiceMatcher = lazy(() =>
  import("@/components/ServiceMatcher").then((m) => ({ default: m.ServiceMatcher })),
);
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";
import logoCudo from "@/assets/logos/cudo.webp";
import logoIntel from "@/assets/logos/intel.webp";
import logoThunderbolt from "@/assets/logos/thunderbolt.webp";
import logoRockland from "@/assets/logos/rockland.webp";
import logoWaypoint from "@/assets/logos/waypoint.webp";
import logoTechThings from "@/assets/logos/techthings.webp";
import { useText } from "@/lib/site-content";
import { getLatestPost } from "@/lib/home.functions";

const projectLogos: Record<string, string[]> = {
  cudo: [logoCudo],
  thunderbolt: [logoIntel, logoThunderbolt],
  rockland: [logoRockland],
  waypoint: [logoWaypoint],
  techthings: [logoTechThings],
};

export const Route = createFileRoute("/")({
  loader: async () => {
    const latestPost = await getLatestPost();
    return { latestPost: latestPost as LatestPost | null };
  },
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Scott Cunningham | Content Strategy Services" },
      {
        name: "description",
        content:
          "Scott Cunningham (@scottcbusiness) offers content strategy, social media, and AI workflow services for AI, SaaS, cloud, Web3, and tech brands.",
      },
      { property: "og:title", content: "Scott Cunningham | Content Strategy Services" },
      {
        property: "og:description",
        content:
          "Scott Cunningham (@scottcbusiness) offers content strategy, social media, and AI workflow services for AI, SaaS, cloud, Web3, and tech brands.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/" },
      { property: "og:image", content: "https://scottcbusiness.com/og-home.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content:
          "Scott Cunningham: clear content systems for complex tech brands, covering content strategy, technical copywriting and AI workflows",
      },
      { name: "twitter:title", content: "Scott Cunningham | Content Strategy Services" },
      {
        name: "twitter:description",
        content:
          "Scott Cunningham (@scottcbusiness) offers content strategy, social media, and AI workflow services for AI, SaaS, cloud, Web3, and tech brands.",
      },
      { name: "twitter:image", content: "https://scottcbusiness.com/og-home.jpg" },
      {
        name: "twitter:image:alt",
        content:
          "Scott Cunningham: clear content systems for complex tech brands, covering content strategy, technical copywriting and AI workflows",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://scottcbusiness.com/#website",
              url: "https://scottcbusiness.com/",
              name: "Scott Cunningham",
              alternateName: ["scottcbusiness", "@scottcbusiness", "scottcbusiness.com", "Scott Cunningham Services"],
              publisher: { "@id": "https://scottcbusiness.com/#person" },
            },
            {
              "@type": "Person",
              "@id": "https://scottcbusiness.com/#person",
              name: "Scott Cunningham",
              alternateName: ["scottcbusiness", "@scottcbusiness", "Scott Cunningham Services"],
              url: "https://scottcbusiness.com/",
              jobTitle: "Content Strategist",
              description: "Scott Cunningham (@scottcbusiness) offers content strategy, social media, and AI workflow services for AI, SaaS, cloud, Web3, and tech brands.",
              sameAs: [
                "https://www.linkedin.com/in/scottcbusiness/",
                "https://x.com/scottcbusiness",
                "https://bsky.app/profile/scottcbusiness.com",
              ],
            },
            {
              "@type": "ProfessionalService",
              "@id": "https://scottcbusiness.com/#business",
              name: "scottcbusiness",
              alternateName: ["scottcbusiness", "@scottcbusiness", "Scott Cunningham Services"],
              url: "https://scottcbusiness.com/",
              founder: { "@id": "https://scottcbusiness.com/#person" },
              areaServed: "Worldwide",
              serviceType: ["Content Strategy", "Social Media Strategy", "AI Content Workflows", "Copywriting"],
            },
          ],
        }),
      },
    ],
    links: [
      { rel: "canonical", href: "https://scottcbusiness.com/" },
      {
        rel: "preload",
        as: "image",
        href: portrait.img.src,
        imageSrcSet: portrait.sources.webp,
        imageSizes: "(min-width: 1280px) 528px, 75vw",
        fetchPriority: "high" as const,
      },
    ],
  }),
  component: Index,
});

type LatestPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  published_at: string | null;
  category: { name: string; slug: string } | null;
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

type PathLink = { label: string; to?: string; hash?: string; href?: string };

function PathCard({
  eyebrow,
  title,
  body,
  primary,
  links,
  accent,
}: {
  eyebrow: string;
  title: ReactNode;
  body: string;
  primary: { label: string; to: string };
  links: PathLink[];
  accent: string;
}) {
  return (
    <div
      className="group relative flex h-full flex-col overflow-hidden rounded-md border-2 border-foreground bg-background p-5 shadow-pop transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_oklch(0.16_0.02_280/0.45)] sm:flex-row sm:gap-6 sm:p-6"
      style={{ ["--path-accent" as string]: accent }}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100"
        style={{ background: accent }}
      />
      <div className="flex flex-col sm:flex-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>
          {eyebrow}
        </p>
        <h2 className="mt-2 font-display text-xl leading-tight text-balance">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <div className="mt-4 flex flex-col sm:mt-0 sm:items-end sm:justify-center sm:text-right">
        <Link
          to={primary.to}
          className="group/link inline-flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.2em] text-foreground sm:self-auto"
        >
          <span className="border-b border-foreground/30 pb-0.5 transition-colors group-hover/link:border-foreground">
            {primary.label}
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
        </Link>
        <ul className="mt-3 space-y-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {links.map((l) => (
            <li key={l.label}>
              {l.href ? (
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
                >
                  <span aria-hidden="true">→</span> {l.label}
                </a>
              ) : (
                <Link
                  to={l.to!}
                  hash={l.hash}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
                >
                  <span aria-hidden="true">→</span> {l.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Index() {
  const { latestPost } = Route.useLoaderData();
  const brandMarquee = useInteractiveMarquee<HTMLDivElement, HTMLDivElement>({
    baseDuration: 90,
  });
  const heroEyebrow = useText("home.hero.eyebrow");
  const heroName = useText("home.hero.name");
  const heroSubtitle = useText("home.hero.subtitle");
  const heroBody = useText("home.hero.body");
  const heroPickPath = useText("home.hero.pickPath");
  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden border-b border-border grain">
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        {/* Color blobs */}
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s" }}
        />
        <div
          className="blob float-slow bottom-[-160px] left-1/3 h-[480px] w-[480px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-6s" }}
        />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 pb-16 pt-20 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:pb-28 lg:pt-24">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              {heroEyebrow}
            </p>
            <h1 className="mt-6 font-display leading-[0.95]">
              <span
                className="block pb-4 text-[clamp(2.5rem,7vw,6rem)] text-gradient text-shadow-hard-white [overflow-wrap:normal]"
                style={{ lineHeight: 1.1 }}
              >
                {heroName}
              </span>
              <span aria-hidden="true" className="my-4 block h-px w-16 bg-border" />
              <span className="block text-[clamp(1.25rem,2.5vw,1.75rem)] font-normal leading-snug text-muted-foreground">
                {heroSubtitle}
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {heroBody}
            </p>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              {heroPickPath}
            </p>
            <div className="mt-4 grid gap-4">
              <PathCard
                eyebrow="I'm hiring for a role"
                title={
                  <>
                    View <em className="italic text-gradient">resume</em> &amp; experience
                  </>
                }
                body="Resume, case studies, and a recruiter-friendly summary."
                primary={{ label: "See resume", to: "/resume" }}
                links={[
                  { label: "Case studies", to: "/work" },
                  { label: "About", to: "/about" },
                ]}
                accent="var(--color-primary)"
              />
              <PathCard
                eyebrow="I need content strategy & execution"
                title={
                  <>
                    Find the right <em className="italic text-gradient">service</em>
                  </>
                }
                body="Services, selected work, and a matcher to scope what fits."
                primary={{ label: "Explore services", to: "/services" }}
                links={[
                  { label: "Selected work", to: "/work" },
                  { label: "Service matcher", to: "/services", hash: "matcher" },
                ]}
                accent="var(--color-electric)"
              />
              <PathCard
                eyebrow="I follow Scott's content"
                title={
                  <>
                    Tune in to <em className="italic text-gradient">Tech &amp; Things</em>
                  </>
                }
                body="Tech & Things episodes, Field Notes blog, and socials."
                primary={{ label: "Explore media", to: "/media" }}
                links={[
                  { label: "Read the blog", to: "/blog" },
                  {
                    label: "Tech & Things on YouTube",
                    href: "https://www.youtube.com/@scottcbusiness",
                  },
                ]}
                accent="var(--color-violet)"
              />
            </div>
          </div>
          <div className="relative lg:col-span-5 lg:flex lg:flex-col">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 -z-10 h-32 w-32 rounded-full bg-[var(--color-electric)] blur-2xl opacity-70 hidden xl:block" />
              <ParallaxTilt className="block">
                <Link
                  to="/about"
                  aria-label="About Scott Cunningham"
                  className="group relative mx-auto block aspect-square w-3/4 max-w-[630px] overflow-hidden rounded-full transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-105 xl:mx-auto xl:aspect-[4/5] xl:w-[92%] xl:max-w-[528px] xl:rounded-sm xl:border-2 xl:border-foreground xl:shadow-pop xl:hover:scale-100 xl:hover:shadow-[0_28px_56px_-20px_oklch(0.16_0.02_280/0.5)] min-[1400px]:w-[95%] min-[1700px]:w-[98%]"
                >
                  <img
                    src={portrait.img.src}
                    srcSet={portrait.sources.webp}
                    sizes="(min-width: 1280px) 528px, 75vw"
                    alt="Portrait of Scott Cunningham"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out xl:group-hover:scale-[1.04]"
                    width={portrait.img.w}
                    height={portrait.img.h}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </Link>
              </ParallaxTilt>
            </div>
            <div className="mt-6 flex justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.25em] text-foreground/80 transition-colors hover:text-foreground"
              >
                <span className="relative">
                  Get in touch
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-0.5 left-0 h-px w-full bg-foreground/30 transition-colors group-hover:bg-foreground"
                  />
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <div className="mt-10 hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
              <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                More about Scott
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Link
                  to="/about"
                  className="group flex items-center justify-between gap-1 rounded-md border-2 border-foreground bg-background px-2.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
                >
                  About
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/blog"
                  className="group flex items-center justify-between gap-1 rounded-md border-2 border-foreground bg-background px-2.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
                >
                  Blog
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/resume"
                  className="group flex items-center justify-between gap-1 rounded-md border-2 border-foreground bg-background px-2.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
                >
                  Resume
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
              <div className="mt-8">
                <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  Socials
                </p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {[
                    {
                      href: "https://www.linkedin.com/in/scottcbusiness/",
                      label: "LinkedIn",
                      Icon: Linkedin,
                    },
                    { href: "https://x.com/scottcbusiness", label: "X", Icon: XIcon },
                    {
                      href: "https://www.youtube.com/@scottcbusiness",
                      label: "YouTube",
                      Icon: Youtube,
                    },
                    {
                      href: "https://bsky.app/profile/scottcbusiness.com",
                      label: "Bluesky",
                      Icon: BlueskyIcon,
                    },
                    {
                      href: "https://www.facebook.com/ScottCBusiness/",
                      label: "Facebook",
                      Icon: Facebook,
                    },
                    { href: "/blog", label: "Blog", Icon: BookOpen },
                    {
                      href: "https://calendly.com/scottthomascunningham/30min",
                      label: "Book a meeting",
                      Icon: CalendarDays,
                    },
                    { href: "mailto:scottcbusiness@protonmail.com", label: "Email", Icon: Mail },
                    { href: "/rss.xml", label: "RSS", Icon: Rss },
                  ].map(({ href, label, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel={href.startsWith("http") ? "noreferrer" : undefined}
                      aria-label={label}
                      title={label}
                      className="grid h-10 w-10 place-items-center rounded-full border-2 border-foreground bg-background text-foreground transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
              {latestPost && (
                <div className="mt-8 hidden flex-1 flex-col min-h-0 lg:flex xl:hidden">
                  <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    Scott's Latest Blog Post
                  </p>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: latestPost.slug }}
                    className="group mt-4 flex flex-1 min-h-0 flex-col overflow-hidden rounded-md border-2 border-foreground bg-background p-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_oklch(0.16_0.02_280/0.4)]"
                  >
                    {latestPost.category && (
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        {latestPost.category.name}
                      </p>
                    )}
                    <p className="mt-1 font-display text-lg leading-snug text-balance">
                      {latestPost.title}
                    </p>
                    {latestPost.cover_image_url && (
                      <div className="relative mt-3 hidden min-h-0 flex-1 overflow-hidden rounded-sm border border-foreground/15 lg:block xl:hidden">
                        <img
                          src={transformSupabaseImage(latestPost.cover_image_url, { width: 400 })}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          width={400}
                          height={250}
                          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
                        />
                        <div className="absolute inset-0 bg-background/20" aria-hidden="true" />
                        <img
                          src={transformSupabaseImage(latestPost.cover_image_url, { width: 600 })}
                          srcSet={supabaseSrcSet(latestPost.cover_image_url, [400, 600, 900])}
                          sizes="(min-width: 1024px) 30vw, 100vw"
                          alt=""
                          loading="lazy"
                          width={600}
                          height={375}
                          className="relative h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      <span>
                        {latestPost.published_at ? formatDate(latestPost.published_at) : ""}
                        {" · "}
                        {readingTimeMinutes(latestPost.content)} min read
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative flex justify-center pb-8">
          <p className="font-mono text-base uppercase tracking-[0.3em] text-muted-foreground">
            Trusted by
          </p>
        </div>

        {/* Marquee */}
        <div
          ref={brandMarquee.containerRef}
          className="relative overflow-hidden border-y-2 border-foreground bg-gradient-night py-5 text-background"
        >
          <div
            ref={brandMarquee.trackRef}
            className="marquee flex w-max whitespace-nowrap font-display text-3xl"
          >
            {[0, 1].map((g) => {
              const isDup = g === 1;
              return (
                <div
                  key={g}
                  className="flex shrink-0 items-center gap-12 pr-12"
                  {...(isDup ? { "aria-hidden": true, inert: true } : {})}
                >
                  {brands.map((b) => (
                    <span key={b.name} className="flex items-center gap-12">
                      {isDup ? (
                        <span className="transition-all duration-300 ease-out">{b.name}</span>
                      ) : (
                        <a
                          href={b.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="transition-all duration-300 ease-out hover:scale-110 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.55)]"
                        >
                          {b.name}
                        </a>
                      )}{" "}
                      <span className="text-[var(--color-electric)]">✦</span>
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <LightSectionBackdrop variant="analytics" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Selected Work
              </p>
              <h2 className="mt-4 font-display text-4xl md:text-6xl">Case studies.</h2>
            </div>
            <Link
              to="/work"
              className="hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] md:inline-flex"
            >
              All work <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-16 divide-y divide-border border-y border-border">
            {projects.map((p, i) => (
              <Link
                key={p.slug}
                to="/work/$slug"
                params={{ slug: internalToPublicSlug[p.slug] ?? p.slug }}
                className="group grid grid-cols-12 items-center gap-4 px-2 py-8 transition-all duration-300 ease-out hover:scale-[1.015] hover:bg-muted/50 hover:shadow-[0_18px_40px_-24px_oklch(0.16_0.02_280/0.35)]"
              >
                <span className="col-span-1 font-mono text-xs text-muted-foreground">0{i + 1}</span>
                <span className="col-span-2 flex items-center gap-1.5 md:col-span-2">
                  {projectLogos[p.slug]?.map((src) => (
                    <span
                      key={src}
                      className="flex h-10 w-10 items-center justify-center rounded-md border-2 border-foreground bg-background p-1 shadow-pop md:h-12 md:w-12"
                    >
                      <img
                        src={src}
                        alt={`${p.name} brand logo`}
                        className="h-full w-full object-contain"
                        width={48}
                        height={48}
                        decoding="async"
                        loading="lazy"
                        fetchPriority="auto"
                      />
                    </span>
                  ))}
                </span>
                <span className="col-span-9 font-display text-3xl md:col-span-5 md:text-5xl">
                  {p.name}
                </span>
                <span className="col-span-7 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground md:col-span-2">
                  {p.sector}
                </span>
                <span className="col-span-4 text-right font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground md:col-span-1">
                  {p.year}
                </span>
                <span className="col-span-1 text-right">
                  <ArrowUpRight className="ml-auto h-5 w-5 transition-transform group-hover:rotate-45" />
                </span>
              </Link>
            ))}
          </div>

          <div className="group mt-12 flex flex-wrap items-center justify-between gap-6 rounded-md border-2 border-foreground bg-gradient-acid p-8 shadow-pop transition-all duration-300 hover:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.18)] hover:scale-[1.01]">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Want this for your brand?
              </p>
              <p className="mt-3 font-display text-2xl leading-tight md:text-3xl">
                <span className="shine-text">
                  Need this kind of <em className="italic text-gradient">content system</em>?
                </span>
              </p>
            </div>
            <Link
              to="/contact"
              className="lift-glow group/link inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)]"
            >
              Scope your content system{" "}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover/link:rotate-45" />
            </Link>
          </div>
        </div>
      </section>

      <SparkleDivider />

      {/* SERVICES */}
      <LazyOnView minHeight={720}>
        <Suspense fallback={null}>
          <ServiceMatcher />
        </Suspense>
      </LazyOnView>

      {/* LATEST FROM THE BLOG */}
      {latestPost && (
        <section className="relative isolate overflow-hidden bg-foreground text-background">
          <DarkSectionBackdrop />
          <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
            <div className="grid gap-x-8 gap-y-4 lg:grid-cols-[1.4fr_1fr]">
              <div className="flex flex-col" />
              <div className="flex items-end justify-end">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white"
                >
                  All posts <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <article className="group relative mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.06] focus-within:ring-1 focus-within:ring-[var(--color-primary)]/40 sm:p-8">
              <span
                aria-hidden="true"
                className="nudge-right pointer-events-none absolute -left-12 top-1/2 hidden -translate-y-1/2 lg:flex"
              >
                <ArrowRight className="h-8 w-8 text-[var(--color-primary)] drop-shadow-[0_0_14px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]" />
              </span>
              <div className="grid gap-x-8 gap-y-3 lg:grid-cols-[1.4fr_1fr]">
                <div className="flex flex-col">
                  <h2
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                    className="glow-violet-hover inline-flex origin-left items-center gap-2.5 text-3xl font-bold uppercase tracking-tight text-white transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:scale-[1.02] lg:text-4xl"
                  >
                    <Megaphone
                      aria-hidden="true"
                      className="h-7 w-7 lg:h-8 lg:w-8 text-[var(--color-primary)] drop-shadow-[0_0_10px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]"
                    />
                    Featured Blog Post
                  </h2>
                  <div
                    aria-hidden="true"
                    className="gradient-sweep mt-3 h-0.5 w-full rounded-full"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265), oklch(0.6 0.28 305), oklch(0.66 0.26 28))",
                    }}
                  />
                </div>
                <span className="hidden lg:block" aria-hidden="true" />
              </div>
              <div className="mt-6 grid gap-x-8 gap-y-6 lg:grid-cols-[1.4fr_1fr]">
                {latestPost.cover_image_url && (
                  <div>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: latestPost.slug }}
                      viewTransition
                      className="block"
                      preload="intent"
                    >
                      <div className="card-img-sweep relative rounded-md bg-white/5">
                        <img
                          src={transformSupabaseImage(latestPost.cover_image_url, {
                            width: 1280,
                            format: "webp",
                            quality: 78,
                          })}
                          srcSet={supabaseSrcSet(latestPost.cover_image_url, [480, 800, 1280])}
                          alt={latestPost.title}
                          loading="lazy"
                          decoding="async"
                          width={1280}
                          height={800}
                          sizes="(min-width: 1024px) 720px, 100vw"
                          className="relative h-auto w-full rounded-md object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03] group-hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.55)]"
                        />
                      </div>
                    </Link>
                    {latestPost.category && (
                      <div className="mt-4">
                        <span className="inline-flex items-center rounded-full border border-white/30 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
                          {latestPost.category.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col">
                  <Link
                    to="/blog/$slug"
                    params={{ slug: latestPost.slug }}
                    viewTransition
                    className="block"
                    preload="intent"
                  >
                    <h3
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                      className="glow-violet-hover origin-left text-3xl font-bold tracking-tight text-white transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:scale-[1.02] lg:text-4xl"
                    >
                      {latestPost.title}
                    </h3>
                    {latestPost.excerpt && (
                      <p className="mt-3 text-base text-white/75">{latestPost.excerpt}</p>
                    )}
                  </Link>
                  <Link
                    to="/about"
                    className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white"
                  >
                    <img
                      src={scottAvatar}
                      alt=""
                      width={28}
                      height={28}
                      loading="lazy"
                      decoding="async"
                      className="h-7 w-7 rounded-full border border-white/20 object-cover"
                    />
                    <span className="text-[11px] font-medium tracking-[0.18em] uppercase">
                      Scott Cunningham
                    </span>
                  </Link>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                    {latestPost.published_at && (
                      <time dateTime={latestPost.published_at}>
                        {formatDate(latestPost.published_at)}
                      </time>
                    )}
                    {latestPost.published_at && <span aria-hidden="true">·</span>}
                    <span>
                      {readingTimeMinutes(latestPost.content || latestPost.excerpt || "")} min read
                    </span>
                  </div>
                  <div className="mt-6">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: latestPost.slug }}
                      viewTransition
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:underline"
                      preload="intent"
                    >
                      Read post <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* APPROACH */}
      <section className="relative isolate overflow-hidden bg-gradient-acid">
        <LightSectionBackdrop variant="blueprint" arcs={false} />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-12 lg:px-10 lg:py-28">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-foreground/70">
              Approach
            </p>
            <h2 className="mt-6 pb-4 font-display text-4xl leading-[1] lg:text-5xl">
              Content as <br />
              <em className="italic text-gradient">infrastructure.</em>
            </h2>
            <dl className="mt-10 space-y-5 border-l-2 border-foreground/20 pl-5">
              {[
                {
                  k: "01",
                  t: "Strategy first",
                  d: "Audience, narrative, and channels mapped to business outcomes.",
                },
                {
                  k: "02",
                  t: "Editorial system",
                  d: "Repeatable workflows that compound across formats and surfaces.",
                },
                {
                  k: "03",
                  t: "Performance reporting",
                  d: "Every post tied back to pipeline, conversion, or retention.",
                },
              ].map((p) => (
                <div key={p.k}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/60">
                    {p.k} · {p.t}
                  </dt>
                  <dd className="mt-1 text-sm leading-relaxed text-foreground/80">{p.d}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-7">
            <p className="font-display text-2xl leading-[1.25] text-balance md:text-3xl lg:text-4xl">
              I treat content as{" "}
              <em className="italic" style={{ color: "var(--color-violet)" }}>
                infrastructure
              </em>
              , an editorial system that compounds. Strategy first, copy that earns its place, and
              reporting that ties every post back to revenue.
            </p>
            <Reveal
              as="div"
              className="mt-10 grid grid-cols-3 stat-grid gap-px rounded-md border-2 border-foreground bg-foreground shadow-pop"
            >
              {[
                { v: "10+ yrs", l: "In-market experience" },
                { v: "120+", l: "Accounts managed" },
                { v: "$1M → $100M", l: "Scale-up period supported" },
              ].map((s) => (
                <StatTile key={s.l} value={s.v} label={s.l} />
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <SparkleDivider />

      {/* TESTIMONIALS - lazy-loaded below the fold */}
      <LazyOnView minHeight={640}>
        <Suspense fallback={null}>
          <Testimonials
            className="border-t-0"
            eyebrow="Testimonials"
            heading={
              <h2 className="mt-6 font-display text-[clamp(2rem,5vw,4rem)] leading-[1.1] text-balance md:whitespace-nowrap">
                Words from people I've{" "}
                <em className="whitespace-nowrap italic text-gradient">shipped with</em>.
              </h2>
            }
          />
        </Suspense>
      </LazyOnView>
    </>
  );
}
